import {Dialog, Transition} from "@headlessui/react";
import {BlackButton} from "../../Buttons/BlackButton";
import {Fragment, useEffect, useState} from "react";
import {PrimaryButton} from "../../Buttons/PrimaryButton";
import {useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import * as yup from "yup";
import {SelectInput} from "../../Form/SelectInput";
import {useBoundStore} from "../../../Store/store";
import dayjs from "dayjs";
import {useTranslation} from "react-i18next";

export const GenerateContractForShiftDialog = (props) => {
    const {
        open,
        onClose,
        onSuccess,
        userId,
        branchId
    } = props;

    const {t} = useTranslation();
    const [loading, setLoading] = useState(false);
    const [shifts, setShifts] = useState([]);
    const [loadingShifts, setLoadingShifts] = useState(false);

    const getShiftsForUser = useBoundStore((state) => state.getShiftsForUser);
    const regenerateContractForShift = useBoundStore((state) => state.regenerateContractForShift);

    const schema = yup.object({
        shift_id: yup.string().required(t('contracts.select_shift')),
    }).required();

    const {
        handleSubmit,
        formState: {errors},
        setValue,
        control,
        reset
    } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            shift_id: null,
        }
    });

    useEffect(() => {
        if (open && userId) {
            const fetchShifts = async () => {
                setLoadingShifts(true);
                const result = await getShiftsForUser(userId, 1, null, null, null, null, branchId, true);

                if (result && result.data) {
                    const confirmedShifts = result.data.filter(shift => {
                        return shift.freelancer !== null && shift.freelancer !== undefined && shift.freelancer !== '';
                    });
                    setShifts(confirmedShifts);
                } else {
                    setShifts([]);
                }

                setLoadingShifts(false);
            };

            fetchShifts();
        }
    }, [open, userId, branchId, getShiftsForUser]);

    const onSubmit = async (data) => {
        setLoading(true);
        const success = await regenerateContractForShift(data.shift_id);
        setLoading(false);

        if (success) {
            reset();
            onSuccess();
            onClose();
        }
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    const shiftOptions = shifts.map(shift => {
        const startTime = shift.start_time ? dayjs(shift.start_time).format('DD/MM/YYYY HH:mm') : 'Unknown';
        const endTime = shift.end_time ? dayjs(shift.end_time).format('HH:mm') : 'Unknown';
        const freelancerName = `${shift.freelancer?.first_name || ''} ${shift.freelancer?.last_name || ''}`.trim() || 'Unknown';

        return {
            value: shift.id.toString(),
            label: `${startTime} - ${endTime} (${freelancerName})`
        };
    });

    return (
        <>
            <Transition appear show={open} as={Fragment}>
                <Dialog as="div" className="relative z-10" onClose={handleClose}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-black/50"/>
                    </Transition.Child>

                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4 text-center">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 scale-95"
                                enterTo="opacity-100 scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 scale-100"
                                leaveTo="opacity-0 scale-95"
                            >
                                <Dialog.Panel
                                    className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                                    <Dialog.Title
                                        as="h3"
                                        className="text-lg font-medium leading-6 text-gray-900"
                                    >
                                        {t('contracts.generate_for_shift_title')}
                                    </Dialog.Title>
                                    <div className="mt-4">
                                        <p className="text-sm text-gray-500 mb-4">
                                            {t('contracts.generate_for_shift_description')}
                                        </p>

                                        {loadingShifts ? (
                                            <div className="flex justify-center items-center py-8">
                                                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
                                            </div>
                                        ) : (
                                            <form onSubmit={handleSubmit(onSubmit)}>
                                                <SelectInput
                                                    name="shift_id"
                                                    control={control}
                                                    setValue={setValue}
                                                    translationIndex={t('contracts.select_shift')}
                                                    options={shiftOptions}
                                                    errors={errors}
                                                    clearable={true}
                                                />

                                                {shiftOptions.length === 0 && (
                                                    <p className="text-sm text-red-500 mb-4">
                                                        {t('contracts.no_confirmed_shifts')}
                                                    </p>
                                                )}

                                                <div className={'flex justify-end'}>
                                                    <div className="mt-4 grid grid-cols-2 w-fit gap-4">
                                                        <div>
                                                            <BlackButton outlined={true} onClick={handleClose} disabled={loading}>
                                                                {t('general.actions.cancel')}
                                                            </BlackButton>
                                                        </div>
                                                        <PrimaryButton
                                                            type="submit"
                                                            disabled={loading || shiftOptions.length === 0}
                                                        >
                                                            {loading ? (
                                                                <div className="flex items-center">
                                                                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                    </svg>
                                                                    {t('general.messages.processing')}
                                                                </div>
                                                            ) : t('contracts.generate_contract_button')}
                                                        </PrimaryButton>
                                                    </div>
                                                </div>
                                            </form>
                                        )}
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition>
        </>
    )
}

